import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import {
  X,
  Plus,
  Trash2,
  AlertCircle,
  Loader
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { getCategories } from '../../services/caregory';
import { getPartnersList } from '../../services/merchant';

interface EMIOption {
  months: number;
  downpayment: number;
}

interface ProductAttribute {
  key: string;
  values: string[];
}

interface ProductSpecification {
  key: string;
  value: string;
}

interface ProductVariation {
  attributes: { [key: string]: string };
  mrp: number;
  price: number;
  id?: string;
}

interface FormData {
  name: string;
  description: string;
  brand: string;
  modelCode?: string;
  category: string;
  subCategory?: string;
  mrpCheck: boolean;
  serialNoValidationLevel: number;
  attributes: ProductAttribute[];
  variations: ProductVariation[];
}

// List of common attribute keys as suggestions
const attributeKeySuggestions = [
  'Color',
  'Size',
  'Material',
  'Weight',
  'Dimensions',
  'Storage',
  'RAM',
  'Processor',
  'Battery',
  'Display',
  'Camera',
  'Water Resistance',
];

const categoryOptions = [
  { value: 'CDR', label: 'CDR - Consumer Durables. Appliances, Panel, Etc' },
  { value: 'MOB', label: 'MOB - Smart Phones' },
  { value: 'E2V', label: 'E2V - Electric Vehicles (2 Wheeler)' },
  { value: 'VAS', label: 'VAS - Value Added Services' },
  { value: 'CAM', label: 'CAM - Camera' },
  { value: 'LAP', label: 'LAP - Laptop, Desktops and Computers' },
  { value: 'INS', label: 'INS - Insurance' },
  { value: 'MED', label: 'MED - Medical' },
  { value: 'EDU', label: 'EDU - Educational' },
  { value: 'TRV', label: 'TRV - Travel' },
  { value: 'FIT', label: 'FIT - Fitness Equipment' },
  { value: 'CYC', label: 'CYC - Bicycle' },
  { value: 'MAT', label: 'MAT - Mattress' },
  { value: 'HOM', label: 'HOM - Home Improvement , Home decor , Interior, Modular Kitchen' },
  { value: 'FUR', label: 'FUR - Furniture' },
  { value: 'FAS', label: 'FAS - Fashion' },
  { value: 'JWL', label: 'JWL - Jewellery' },
  { value: 'KTN', label: 'KTN - Kitchen Appliances' },
  { value: 'AUD', label: 'AUD - Audio Digital, Speakers' },
  { value: 'WAT', label: 'WAT - Watch, Smart Watches' },
  { value: 'GRM', label: 'GRM - Personal Care, Beautification, Grooming' },
  { value: 'INT', label: 'INT - Internet and Broadband Services' },
  { value: 'CAR', label: 'CAR - Car Accessories' },
  { value: 'KID', label: 'KID - Toys and Kids Products' },
  { value: 'MUS', label: 'MUS - Musical Instruments' },
  { value: 'OTT', label: 'OTT - ' },
  { value: 'SLR', label: 'SLR - Solar Panels' },
  { value: 'GRO', label: 'GRO - Grocery' },
  { value: 'PET', label: 'PET - Pets, Equipment, food Etc' },
  { value: 'HOS', label: 'HOS - Hospitality' },
];

interface AddProductFormProps {
  onSubmit: (data: FormData) => void;
  onCancel: () => void;
  initialData?: Partial<FormData>;
  isSubmitting?: boolean;
}

// Utility function to capitalize the first letter of each word
const capitalizeWords = (str: string): string => {
  return str.replace(/\b\w+\b/g, (word) => word.toUpperCase());
};

const AddProductForm: React.FC<AddProductFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    brand: initialData?.brand || '',
    modelCode: initialData?.modelCode || '',
    category: initialData?.category || '',
    subCategory: initialData?.subCategory || '',
    mrpCheck: initialData?.mrpCheck || false,
    serialNoValidationLevel: initialData?.serialNoValidationLevel || 0,
    attributes: initialData?.attributes || [{ key: '', values: [] }],
    variations: initialData?.variations || [{ attributes: {}, mrp: 0, price: 0 }],
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [brandError, setBrandError] = useState<string>('');
  const [availableAttributes, setAvailableAttributes] = useState<{ [key: string]: string[] }>({});
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  const [attributeKeys, setAttributeKeys] = useState<string[]>([]);
  const [selectedAttrKey, setSelectedAttrKey] = useState('');
  const [newAttrKey, setNewAttrKey] = useState('');
  const [attributeKeyTabs, setAttributeKeyTabs] = useState<string[]>([]);
  const [attributeValueInput, setAttributeValueInput] = useState('');
  const [currentVariation, setCurrentVariation] = useState<ProductVariation>({ attributes: {}, mrp: 0, price: 0 });

  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [subCategoryLoading, setSubCategoryLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);






  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const brands = await getPartnersList(); // 👈 API to fetch brands
        setAvailableBrands(brands);
      } catch (error) {
        console.error("Error fetching brands:", error);
      }
    };

    fetchBrands();
  }, []);


  useEffect(() => {
    setCategoryLoading(true);
    getCategories({ page: 1, pageSize: 100 })
      .then((result) => {
        const options = (result.data || []).map((cat) => ({
          value: cat.code,
          label: `${cat.code} - ${cat.name}`,
        }));
        setCategoryOptions(options);
      })
      .catch(() => setCategoryOptions([]))
      .finally(() => setCategoryLoading(false));
  }, []);

  useEffect(() => {
    if (!formData.category) {
      setSubCategoryOptions([]);
      return;
    }

    setSubCategoryLoading(true);

    getCategories({ categoryId: formData.category })
      .then((result: any) => {
        // 🔹 result is an array (see your API)
        const subCategories = result?.[0]?.subCategory || [];

        if (Array.isArray(subCategories) && subCategories.length > 0) {
          const options = subCategories.map((sub: string) => ({
            value: sub,
            label: sub,
          }));
          setSubCategoryOptions(options);
        } else {
          setSubCategoryOptions([]);
        }
      })
      .catch(() => setSubCategoryOptions([]))
      .finally(() => setSubCategoryLoading(false));
  }, [formData.category]);




  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'brand') {
      // Convert brand name to uppercase
      const uppercaseBrand = value.toUpperCase();
      validateBrandName(uppercaseBrand);

      // Set the uppercase value
      setFormData((prev) => ({
        ...prev,
        [name]: uppercaseBrand,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateBrandName = (brand: string): boolean => {
    if (!brand) {
      setBrandError('Brand name is required');
      return false;
    }

    if (brand.includes(' ')) {
      setBrandError('Brand name should not contain spaces');
      return false;
    }

    // No need to check for uppercase first letter since the entire brand is now uppercase

    setBrandError('');
    return true;
  };

  const handleAddAttributeKey = () => {
    if (newAttrKey && !attributeKeys.includes(newAttrKey)) {
      setAttributeKeys(prev => [...prev, newAttrKey]);
      setAttributeKeyTabs(prev => [...prev, newAttrKey]);
      setNewAttrKey('');
    }
  };

  const handleAddAttributeWithValue = () => {
    if (selectedAttrKey && attributeValueInput.trim()) {
      const attrIndex = formData.attributes.findIndex(attr => attr.key === selectedAttrKey);

      if (attrIndex === -1) {
        // Create new attribute if it doesn't exist
        setFormData(prev => ({
          ...prev,
          attributes: [...prev.attributes, { key: selectedAttrKey, values: [attributeValueInput.trim()] }]
        }));
      } else {
        // Add value to existing attribute
        handleAttributeChange(attrIndex, 'value', attributeValueInput.trim());
      }

      // Reset input
      setAttributeValueInput('');
    }
  };

  const handleAttributeChange = (
    index: number,
    field: 'key' | 'value',
    value: string
  ) => {
    if (field === 'key') {
      setFormData(prev => {
        const newAttributes = [...prev.attributes];
        newAttributes[index].key = value;
        return { ...prev, attributes: newAttributes };
      });
    } else {
      setFormData(prev => {
        const newAttributes = [...prev.attributes];
        if (!newAttributes[index].values.includes(value)) {
          newAttributes[index].values.push(value);
        }

        // Update available attributes
        const newAvailableAttributes = { ...availableAttributes };
        newAvailableAttributes[newAttributes[index].key] = newAttributes[index].values;
        setAvailableAttributes(newAvailableAttributes);

        return { ...prev, attributes: newAttributes };
      });
    }
  };

  const addAttribute = () => {
    setFormData((prev) => ({
      ...prev,
      attributes: [...prev.attributes, { key: '', values: [] }],
    }));
  };

  const removeAttribute = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attributes: prev.attributes.filter((_, i) => i !== index),
    }));
  };


  const handleVariationChange = (
    field: keyof ProductVariation,
    value: number
  ) => {
    setCurrentVariation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addVariation = () => {
    // Add current variation to the list
    if (Object.keys(currentVariation.attributes).length > 0) {
      setFormData((prev) => ({
        ...prev,
        variations: [...prev.variations, currentVariation],
      }));

      // Reset current variation
      setCurrentVariation({ attributes: {}, mrp: 0, price: 0 });
      setSelectedAttrKey('');
    }
  };

  const removeVariation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name) {
      newErrors.name = 'Product name is required';
    }

    if (!formData.brand) {
      newErrors.brand = 'Brand name is required';
    } else if (!validateBrandName(formData.brand)) {
      return false;
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.variations.length === 0 ||
      formData.variations.every(v => Object.keys(v.attributes).length === 0)) {
      newErrors.variations = 'At least one variation is required';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0 || brandError) {
      setShowValidationSummary(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    setShowValidationSummary(false);
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      console.log('Form validated successfully, submitting data:', formData);
      onSubmit(formData);
    } else {
      console.error('Form validation failed', errors);
    }
  };

  const handleEditVariation = (variation: ProductVariation, index: number) => {
    console.log("Editing variation:", variation);

    // 1️⃣ Prefill the variation fields (attribute selections + price fields)
    setCurrentVariation({
      attributes: { ...variation.attributes },
      mrp: variation.mrp,
      price: variation.price,
    });

    // 2️⃣ Prefill the "Attributes of Product" section
    setFormData(prev => {
      // Convert variation attributes {Color: "Blue", Size: "M"} → [{ key: "Color", values: ["Blue"] }, ...]
      const variationAttributes = Object.entries(variation.attributes).map(([key, value]) => ({
        key,
        values: [String(value)],
      }));

      // Merge with existing attributes (so old ones are kept)
      const mergedAttributes = [...prev.attributes];

      variationAttributes.forEach(va => {
        const existingIndex = mergedAttributes.findIndex(a => a.key === va.key);
        if (existingIndex !== -1) {
          // Update existing attribute values (avoid duplicates)
          const mergedValues = Array.from(new Set([...mergedAttributes[existingIndex].values, ...va.values]));
          mergedAttributes[existingIndex].values = mergedValues;
        } else {
          // Add new attribute key/value if missing
          mergedAttributes.push(va);
        }
      });

      return {
        ...prev,
        attributes: mergedAttributes,
        variations: prev.variations.filter((_, i) => i !== index), // temporarily remove the one being edited
      };
    });

    // 3️⃣ Auto-select the first attribute key and value for editing (optional)
    const firstAttrKey = Object.keys(variation.attributes)[0];
    const firstAttrValue = variation.attributes[firstAttrKey];
    setSelectedAttrKey(firstAttrKey || "");
    setAttributeValueInput(firstAttrValue || "");

    // (Optional) track edit mode to later “Save” instead of “Add”
    setEditingIndex(index);
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 no-scrollbar ${isSubmitting ? 'opacity-80' : ''}`}>
      {/* Validation Summary */}
      {showValidationSummary && Object.keys(errors).length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
          <div className="flex items-center mb-2">
            <AlertCircle size={20} className="text-red-600 mr-2" />
            <h3 className="text-red-700 font-medium">Please fix the following errors:</h3>
          </div>
          <ul className="list-disc ml-8 text-sm text-red-600">
            {Object.entries(errors).map(([field, message]) => (
              <li key={field}>{message}</li>
            ))}
            {brandError && <li>{brandError}</li>}
          </ul>
        </div>
      )}

      {/* Product Information */}
      <div className="space-y-4">
        <h3 className="text-md font-gilroy-semibold text-gray-800">1. Product Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Product Name*
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue ${errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="Enter product name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle size={14} className="mr-1" />
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-1">
              Brand Name*
            </label>
            <Select
              id="brand"
              name="brand"
              value={formData.brand ? { value: formData.brand, label: formData.brand } : null}
              onChange={(option) =>
                setFormData({ ...formData, brand: option ? option.value : "" })
              }
              options={availableBrands.map((brand) => ({ value: brand, label: brand }))}
              isClearable
              isDisabled={isSubmitting}
              placeholder="Search or select brand..."
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: brandError ? "#EF4444" : "#D1D5DB",
                  borderRadius: "0.5rem",
                  minHeight: "42px",
                  boxShadow: "none",
                }),
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: "#9CA3AF",
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: state.isSelected
                    ? "#E0E7FF"
                    : state.isFocused
                      ? "#F3F4F6"
                      : "transparent",
                  color: state.isSelected ? "#4F46E5" : "#374151",
                  ":active": {
                    backgroundColor: "#E0E7FF",
                  },
                }),
              }}
            />
            {brandError && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle size={14} className="mr-1" />
                {brandError}
              </p>
            )}
            {/* <p className="mt-1 text-xs text-gray-500">
              Select a brand (no spaces, first letter uppercase e.g. "Realme")
            </p> */}
          </div>
          <div>
            <label
              htmlFor="modelNumber"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Model Number
            </label>
            <input
              type="text"
              id="modelNumber"
              name="modelNumber"
              value={formData.modelCode || ""}
              onChange={(e) =>
                setFormData({ ...formData, modelCode: e.target.value })
              }
              disabled={isSubmitting}
              placeholder="Enter product model number (optional)"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

        </div>
      </div>

      {/* Product Category */}
      <div className="space-y-4">
        <h3 className="text-md font-gilroy-semibold text-gray-800">2. Product Category</h3>
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            Category*
          </label>
          <Select
            id="category"
            name="category"
            options={categoryOptions}
            isLoading={categoryLoading}
            value={categoryOptions.find(option => option.value === formData.category)}
            onChange={(selectedOption) =>
              handleInputChange({
                target: {
                  name: 'category',
                  value: selectedOption?.value || '',
                },
              } as React.ChangeEvent<HTMLInputElement>)
            }
            classNamePrefix="react-select"
            className={`text-sm ${errors.category ? 'border-red-500 rounded-md' : ''}`}
            isDisabled={isSubmitting || categoryLoading}
            styles={errors.category ? {
              control: (base) => ({
                ...base,
                borderColor: '#f56565',
                boxShadow: '0 0 0 1px #f56565'
              })
            } : undefined}
          />
          {errors.category && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle size={14} className="mr-1" />
              {errors.category}
            </p>
          )}
        </div>
      </div>

      {Array.isArray(subCategoryOptions) && subCategoryOptions.length > 0 && (
        <div className="space-y-4">
          {/* <h3 className="text-md font-gilroy-semibold text-gray-800">Sub Category</h3> */}
          <div>
            <label htmlFor="subCategory" className="block text-sm font-medium text-gray-700 mb-1">
              Sub Category
            </label>
            <Select
              id="subCategory"
              name="subCategory"
              options={subCategoryOptions}
              isLoading={subCategoryLoading}
              value={subCategoryOptions.find(option => option.value === formData.subCategory)}
              onChange={(selectedOption) =>
                handleInputChange({
                  target: {
                    name: "subCategory",
                    value: selectedOption?.value || "",
                  },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              classNamePrefix="react-select"
              className={`text-sm ${errors.subCategory ? "border-red-500 rounded-md" : ""}`}
              isDisabled={!formData.category || subCategoryLoading || isSubmitting}
              styles={
                errors.subCategory
                  ? {
                    control: (base) => ({
                      ...base,
                      borderColor: "#f56565",
                      boxShadow: "0 0 0 1px #f56565",
                    }),
                  }
                  : undefined
              }
              placeholder={formData.category ? "Select Sub Category" : "Select Category first"}
            />
            {errors.subCategory && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle size={14} className="mr-1" />
                {errors.subCategory}
              </p>
            )}
          </div>
        </div>
      )}



      {/* Product Settings */}
      <div className="space-y-4">
        <h3 className="text-md font-gilroy-semibold text-gray-800">3. Product Settings</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="mrpCheck"
              name="mrpCheck"
              checked={formData.mrpCheck}
              onChange={(e) =>
                setFormData(prev => ({
                  ...prev,
                  mrpCheck: e.target.checked
                }))
              }
              disabled={isSubmitting}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="mrpCheck" className="text-sm font-medium text-gray-700">
              Enable MRP Check
            </label>
          </div>

          <div>
            <label htmlFor="serialNoValidationLevel" className="block text-sm font-medium text-gray-700 mb-1">
              Serial Number Validation Level
            </label>
            <select
              id="serialNoValidationLevel"
              name="serialNoValidationLevel"
              value={formData.serialNoValidationLevel}
              onChange={(e) =>
                setFormData(prev => ({
                  ...prev,
                  serialNoValidationLevel: parseInt(e.target.value)
                }))
              }
              disabled={isSubmitting}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue border-gray-300"
            >
              <option value={0}>Level 0 - No Validation</option>
              <option value={1}>Level 1 - Basic Validation</option>
              <option value={2}>Level 2 - Standard Validation</option>
              {/* <option value={3}>Level 3 - Enhanced Validation</option> */}
            </select>
          </div>
        </div>
      </div>

      {/* Product Attributes */}
      <div className="space-y-4">
        <h3 className="text-md font-gilroy-semibold text-gray-800">4. Attributes of Product</h3>

        {/* First row - Add new attribute key */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newAttrKey}
            onChange={(e) => setNewAttrKey(capitalizeWords(e.target.value))}
            placeholder="Enter Attribute Key"
            disabled={isSubmitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isSubmitting) {
                e.preventDefault();
                handleAddAttributeKey();
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddAttributeKey}
            disabled={isSubmitting}
            className="flex-shrink-0"
          >
            <Plus size={16} />
          </Button>
        </div>

        {/* Display attribute key tabs */}
        {attributeKeyTabs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {attributeKeyTabs.map((key) => (
              <div
                key={key}
                className={`px-4 py-2 rounded-md cursor-pointer text-sm ${selectedAttrKey === key
                  ? 'bg-brand-blue text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                onClick={() => setSelectedAttrKey(key)}
              >
                {key}
              </div>
            ))}
          </div>
        )}

        {/* Second row - Select attribute key and add value */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <select
              value={selectedAttrKey}
              onChange={(e) => setSelectedAttrKey(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
            >
              <option value="">Select Attribute Key</option>
              {attributeKeys.map((key) => (
                <option key={key} value={key}>{key}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={attributeValueInput}
              onChange={(e) => setAttributeValueInput(e.target.value)}
              placeholder="Attribute Value"
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isSubmitting) {
                  e.preventDefault();
                  handleAddAttributeWithValue();
                }
              }}
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleAddAttributeWithValue}
              disabled={isSubmitting || !selectedAttrKey || !attributeValueInput.trim()}
              className="whitespace-nowrap"
            >
              Add Attribute
            </Button>
          </div>
        </div>

        {/* Display added attributes with values */}
        <div className="mt-4 space-y-4">
          {formData.attributes.map((attr, index) => (
            attr.key && attr.values.length > 0 && (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-700 mb-2">{attr.key}</div>
                <div className="flex flex-wrap gap-2">
                  {attr.values.map((value, valueIndex) => (
                    <span
                      key={valueIndex}
                      className="px-3 py-1 bg-white border border-gray-200 rounded-md text-sm flex items-center gap-2"
                    >
                      {value}
                      <button
                        type="button"
                        onClick={() => {
                          const newValues = attr.values.filter((_, i) => i !== valueIndex);
                          setFormData(prev => {
                            const newAttributes = [...prev.attributes];
                            newAttributes[index].values = newValues;
                            return { ...prev, attributes: newAttributes };
                          });
                        }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </div>


      {/* Product Variations */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-md font-gilroy-semibold text-gray-800">6. Product Variations</h3>
        </div>

        {/* Current Variation Form */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {/* Attribute Selectors */}
          {formData.attributes
            .filter(attr => attr.key && attr.values.length > 0)
            .map((attr) => (
              <div key={attr.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {attr.key}
                </label>
                <select
                  value={currentVariation.attributes[attr.key] || ''}
                  onChange={(e) => {
                    setCurrentVariation(prev => ({
                      ...prev,
                      attributes: {
                        ...prev.attributes,
                        [attr.key]: e.target.value
                      }
                    }));
                  }}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                >
                  <option value="">Select {attr.key}</option>
                  {attr.values.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            ))}
        </div>

        {/* Price Fields in Same Line */}
        <div className="flex gap-4 mt-4">
          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MRP (₹)
            </label>
            <input
              type="text"
              value={currentVariation.mrp}
              onChange={(e) => setCurrentVariation(prev => ({
                ...prev,
                mrp: parseFloat(e.target.value) || 0
              }))}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
          </div>

          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MOP (₹)
            </label>
            <input
              type="text"
              value={currentVariation.price}
              onChange={(e) => setCurrentVariation(prev => ({
                ...prev,
                price: parseFloat(e.target.value) || 0
              }))}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
          </div>
        </div>

        <div className="flex justify-end mt-4 gap-3">
          {/* ➕ Add / Update Variation */}
          <Button
            type="button"
            variant="default"
            onClick={() => {
              if (Object.keys(currentVariation.attributes).length === 0) return;

              setFormData(prev => {
                const updatedVariations = [...prev.variations];

                if (editingIndex !== null && editingIndex >= 0) {
                  // 📝 Update existing variation
                  updatedVariations[editingIndex] = {
                    ...currentVariation,
                    id: updatedVariations[editingIndex]?.id || Math.random().toString(36).substring(2, 9)
                  };
                } else {
                  // ✅ Always assign a unique ID when adding
                  const newVariation = {
                    ...currentVariation,
                    id: Math.random().toString(36).substring(2, 9),
                  };

                  // ✅ Prevent accidental duplicate based on same attributes
                  const exists = updatedVariations.some(v =>
                    JSON.stringify(v.attributes) === JSON.stringify(newVariation.attributes)
                  );

                  if (!exists) updatedVariations.push(newVariation);
                  else console.warn("Duplicate variation ignored (same attributes).");
                }

                return { ...prev, variations: updatedVariations };
              });

              // ✅ Reset price fields only
              const savedAttributes = { ...currentVariation.attributes };
              setCurrentVariation({
                attributes: savedAttributes,
                mrp: 0,
                price: 0,
              });

              setEditingIndex(null);
            }}
            disabled={isSubmitting || Object.keys(currentVariation.attributes).length === 0}
          >
            {editingIndex !== null ? "Update Variation" : "Add Variation"}
          </Button>

          {/* 🟢 Duplicate Variation */}
          {editingIndex !== null && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const duplicate = {
                  ...JSON.parse(JSON.stringify(currentVariation)),
                  id: Math.random().toString(36).substring(2, 9),
                };

                setFormData(prev => {
                  // Restore the original variation at its index, and add the duplicate at the end
                  const updatedVariations = [...prev.variations];
                  if (editingIndex !== null && editingIndex >= 0) {
                    // Insert the original back at its index
                    updatedVariations.splice(editingIndex, 0, {
                      ...currentVariation,
                      id: prev.variations[editingIndex]?.id || Math.random().toString(36).substring(2, 9),
                    });
                  }
                  // Add the duplicate at the end
                  return {
                    ...prev,
                    variations: [...updatedVariations, duplicate],
                  };
                });

                const savedAttributes = { ...currentVariation.attributes };
                setCurrentVariation({
                  attributes: savedAttributes,
                  mrp: 0,
                  price: 0,
                });

                setEditingIndex(null);
                // showToast?.success?.("Variation duplicated successfully ✅");
              }}
            >
              Duplicate Variation
            </Button>
          )}
        </div>



        {/* Display Variations as Tags in one line */}
        <div className="flex flex-wrap gap-2">
          {formData.variations.map((variation, index) => {
            const hasAttributes = Object.keys(variation.attributes).length > 0;
            if (!hasAttributes) return null;

            return (
              <div
                key={index}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-md"
              >
                {/* Attribute combinations and prices in one line */}
                <div className="flex items-center gap-2 text-sm">
                  {Object.entries(variation.attributes).map(([key, value], i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-gray-100 rounded text-sm text-gray-700"
                    >
                      {key}: {value}
                    </span>
                  ))}
                  <span className="text-gray-500 ml-2">MRP: ₹{variation.mrp}</span>
                  <span className="text-brand-blue">Price: ₹{variation.price}</span>
                </div>

                {/* ✏️ Edit icon */}
                <button
                  type="button"
                  onClick={() => handleEditVariation(variation, index)}
                  className="text-gray-400 hover:text-blue-500 p-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-pencil"
                  >
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 3 21l.5-4.5L17 3z" />
                  </svg>
                </button>

                {/* ❌ Delete icon */}
                <button
                  type="button"
                  onClick={() => removeVariation(index)}
                  className="text-gray-400 hover:text-red-500 p-1"
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>


        {/* Validation Error for Variations */}
        {errors.variations && (
          <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-md border border-red-200 flex items-center">
            <AlertCircle size={16} className="mr-2 flex-shrink-0" />
            <span>{errors.variations}</span>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={isSubmitting}
          className={isSubmitting ? 'opacity-90' : 'bg-brand-blue text-white py-2 px-4 rounded-lg hover:bg-blue-700'}
          onClick={(e) => {
            console.log('Save Product button clicked');
            e.preventDefault();
            e.stopPropagation(); // Prevent event bubbling

            // Force validation without relying on return value
            const newErrors: Partial<Record<keyof FormData, string>> = {};

            if (!formData.name) {
              newErrors.name = 'Product name is required';
              console.log('Validation failed: missing name');
            }

            if (!formData.brand) {
              newErrors.brand = 'Brand name is required';
              console.log('Validation failed: missing brand');
            } else if (!validateBrandName(formData.brand)) {
              console.log('Validation failed: invalid brand name');
              setBrandError('Brand name invalid');
            }

            if (!formData.category) {
              newErrors.category = 'Category is required';
              console.log('Validation failed: missing category');
            }

            if (formData.variations.length === 0 ||
              formData.variations.every(v => Object.keys(v.attributes).length === 0)) {
              newErrors.variations = 'At least one variation is required';
              console.log('Validation failed: no variations');
            }

            setErrors(newErrors);

            if (Object.keys(newErrors).length > 0 || brandError) {
              // Show validation summary and scroll to top if there are errors
              setShowValidationSummary(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              console.error('Form validation failed', newErrors);
              return;
            }

            // Hide validation summary if no errors
            setShowValidationSummary(false);

            console.log('Form validation passed, submitting data:', formData);

            // Call onSubmit directly, not using any intermediate functions
            try {
              onSubmit(formData);
              console.log('onSubmit function called successfully');
            } catch (error) {
              console.error('Error calling onSubmit:', error);
            }
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <Loader size={16} className="animate-spin mr-2" />
              Saving...
            </span>
          ) : (
            'Save Product'
          )}
        </Button>
      </div>
    </form>
  );
};

export default AddProductForm;